
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Main } from '@/components/layout/main';
import { PageHeader } from '@/components/layout/page-header';

import { superAdminService } from '@/api/super-admin';
import { SuperAdminUser, SuperAdminUserResetPassword, SuperAdminCabinet, SuperAdminUserCreate } from '@/types/super-admin';
import { handleServerError } from '@/utils/handle-server-error';
import {
  Users,
  Eye,
  Shield,
  ShieldOff,
  UserCheck,
  UserX,
  Key,
  Search as SearchIcon,
  MoreHorizontal,
  Calendar,
  Building2,
  Plus,
  Mail,
  User,
  Crown
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

import { useNavigate, useSearch } from '@tanstack/react-router';
import { filterNonNullValues } from '@/lib/utils';
import { timestamp_to_date } from '@/utils/time';


export interface UsersTableParams {
  page: number;
  page_size: number;
  ordering: string | null;
  search: string | null;
  cabinet: string | null;
  include_all: boolean;
}




export default function UsersManagement() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = useSearch({ strict: false });

  const [users, setUsers] = useState<SuperAdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagesCnt, setPagesCnt] = useState(0);
  const [cabinets, setCabinets] = useState<SuperAdminCabinet[]>([]);
  const [selectedUser, setSelectedUser] = useState<SuperAdminUser | null>(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [showResetPasswordDialog, setShowResetPasswordDialog] = useState(false);
  const [showToggleAdminDialog, setShowToggleAdminDialog] = useState(false);
  const [showToggleStatusDialog, setShowToggleStatusDialog] = useState(false);
  const [actionUser, setActionUser] = useState<SuperAdminUser | null>(null);
  const [resetPasswordData, setResetPasswordData] = useState<SuperAdminUserResetPassword>({
    new_password: '',
    confirm_password: ''
  });
  const [searchInput, setSearchInput] = useState<string>((search as any).search || '');
  const [showCreateUserDialog, setShowCreateUserDialog] = useState(false);
  const [createUserData, setCreateUserData] = useState<SuperAdminUserCreate>({
    name: '',
    username: '',
    email: '',
    password: '',
    confirm_password: '',
    cabinet: undefined,
    role: undefined,
    is_active: true
  });

  // Initialize params from URL search parameters
  const [params, setParams] = useState<UsersTableParams>({
    page: Number((search as any).page) || 0,
    page_size: Number((search as any).page_size) || 10,
    ordering: (search as any).ordering || null,
    search: (search as any).search || null,
    cabinet: (search as any).cabinet || null,
    include_all: true,
  });

  // Sync params with URL
  useEffect(() => {
    const filteredParams = filterNonNullValues(params as any);
    navigate({
      search: filteredParams as any,
      replace: true,
    });
  }, [params, navigate]);

  useEffect(() => {
    fetchUsers();
  }, [params]);

  useEffect(() => {
    fetchCabinets();
  }, []);

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      // Reset to first page (offset 0) when search changes
      setParams(prev => ({ ...prev, search: searchInput || null, page: 0 }));
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  const fetchCabinets = async () => {
    try {
      const data = await superAdminService.getCabinets();
      // Handle both direct array and paginated response
      const cabinetsList = Array.isArray(data) ? data : (data as any)?.results || [];
      setCabinets(cabinetsList);
    } catch (error) {
      console.error('Error fetching cabinets:', error);
      handleServerError(error);
      setCabinets([]); // Ensure cabinets is always an array
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      // Convert null values to undefined for API compatibility
      const apiParams: any = {
        ordering: params.ordering || undefined,
        search: params.search || undefined,
        page_size: params.page_size,
        page: params.page,
        cabinet: params.cabinet === "all" ? undefined : params.cabinet || undefined,
      };
      // Always include_all=true and do not enforce is_admin
      apiParams.include_all = true;
      const data = await superAdminService.getUsers(apiParams);
      // Handle both direct array and paginated response
      if (Array.isArray(data)) {
        setUsers(data);
        setPagesCnt(1);
      } else {
        setUsers(data.results || []);
        setPagesCnt(Math.ceil((data.count || 0) / params.page_size));
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      handleServerError(error);
      setUsers([]); // Ensure users is always an array
    } finally {
      setLoading(false);
    }
  };

  const handleViewUser = (user: SuperAdminUser) => {
    setSelectedUser(user);
    setShowDetailsDialog(true);
  };

  const handleResetPassword = async () => {
    if (!actionUser) return;

    if (resetPasswordData.new_password !== resetPasswordData.confirm_password) {
      alert(t('passwords_do_not_match'));
      return;
    }

    try {
      await superAdminService.resetUserPassword(actionUser.id, resetPasswordData);
      setShowResetPasswordDialog(false);
      setActionUser(null);
      setResetPasswordData({ new_password: '', confirm_password: '' });
      alert(t('password_reset_successfully'));
    } catch (error) {
      console.error('Error resetting password:', error);
      handleServerError(error);
    }
  };

  const handleToggleAdmin = async () => {
    if (!actionUser) return;

    try {
      await superAdminService.toggleUserAdmin(actionUser.id);
      setShowToggleAdminDialog(false);
      setActionUser(null);
      fetchUsers();
    } catch (error) {
      console.error('Error toggling admin status:', error);
      handleServerError(error);
    }
  };

  const handleToggleStatus = async () => {
    if (!actionUser) return;

    try {
      await superAdminService.toggleUserStatus(actionUser.id);
      setShowToggleStatusDialog(false);
      setActionUser(null);
      fetchUsers();
    } catch (error) {
      console.error('Error toggling user status:', error);
      handleServerError(error);
    }
  };

  const handleCreateUser = async () => {
    if (createUserData.password !== createUserData.confirm_password) {
      alert(t('passwords_do_not_match'));
      return;
    }

    if (!createUserData.name || !createUserData.password) {
      alert(t('please_fill_required_fields'));
      return;
    }

    try {
      const userData = {
        ...createUserData,
        cabinet: createUserData.cabinet || undefined,
        username: createUserData.username || undefined,
        email: createUserData.email || undefined,
      };

      await superAdminService.createUser(userData);
      setShowCreateUserDialog(false);
      setCreateUserData({
        name: '',
        username: '',
        email: '',
        password: '',
        confirm_password: '',
        cabinet: undefined,
        role: undefined,
        is_active: true
      });
      fetchUsers();
      alert(t('user_created_successfully'));
    } catch (error) {
      console.error('Error creating user:', error);
      handleServerError(error);
    }
  };

  // Users are now filtered server-side, so we use the users array directly
  const filteredUsers = Array.isArray(users) ? users : [];

  if (loading) {
    return (
      <div className="flex h-screen">
        <div className="flex-1 min-w-0">
          <Main>
            <div className='mb-2 flex flex-wrap items-center justify-between gap-x-4 space-y-2'>
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                <p className="mt-2 text-muted-foreground">{t('loading')}</p>
              </div>
            </div>
          </Main>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen">
      <div className="flex-1 min-w-0">

        <Main>
          <div className="space-y-6">
            <PageHeader
              leading={
                <span className="mt-1 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <Users className="h-5 w-5" />
                </span>
              }
              title={t('users_management')}
              description={t('manage_system_users')}
            />

            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="relative flex-1 max-w-sm">
                  <SearchIcon className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder={t('search_users')}
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    className="pl-8"
                  />
                </div>
                <Select
                  value={params.cabinet || 'all'}
                  onValueChange={(value) => setParams(prev => ({ ...prev, cabinet: value === 'all' ? null : value, page: 0 }))}
                >
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder={t('all_cabinets')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('all_cabinets')}</SelectItem>
                    {cabinets.map((cabinet) => (
                      <SelectItem key={cabinet.id} value={cabinet.id.toString()}>
                        {cabinet.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                className="bg-slate-900 hover:bg-slate-800"
                onClick={() => setShowCreateUserDialog(true)}
              >
                <Plus className="h-4 w-4 mr-2" />
                {t('create_user')}
              </Button>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-slate-900">
                  {t('system_users')} ({filteredUsers.length})
                </h2>
              </div>

              {filteredUsers.length === 0 ? (
                <Card>
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <Users className="h-12 w-12 text-slate-400 mb-4" />
                    <h3 className="text-lg font-medium text-slate-900 mb-2">
                      {params.search ? t('no_users_found') : t('no_users_yet')}
                    </h3>
                    <p className="text-slate-600 text-center max-w-md">
                      {params.search
                        ? t('try_different_search_terms')
                        : t('create_first_user_to_get_started')
                      }
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-center">{t('name')}</TableHead>
                          <TableHead className="text-center">{t('email')}</TableHead>
                          <TableHead className="text-center">{t('status')}</TableHead>
                          <TableHead className="text-center">{t('role')}</TableHead>
                          <TableHead className="text-center">{t('last_login')}</TableHead>
                          <TableHead className="w-[120px] text-center">{t('actions')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredUsers.map((user) => (
                          <TableRow key={user.id}>
                            <TableCell className="text-center">
                              <div className="flex flex-col items-center">
                                <span className="font-medium text-nowrap">{user.name}</span>
                                {user.username && (
                                  <span className="text-sm text-muted-foreground text-nowrap">@{user.username}</span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-center">{user.email || '-'}</TableCell>
                            <TableCell className="text-center">
                              <div className="flex flex-col gap-1">
                                <Badge variant={user.is_active ? 'default' : 'secondary'}>
                                  {user.is_active ? t('active') : t('inactive')}
                                </Badge>
                                {user.is_admin && (
                                  <Badge variant="outline" className="text-blue-600">
                                    {t('admin')}
                                  </Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-center">{user.role_name || '-'}</TableCell>
                            <TableCell className="text-center">{user.last_login_display || t('never')}</TableCell>
                            <TableCell className="text-center">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" className="h-8 w-8 p-0">
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => handleViewUser(user)}>
                                    <Eye className="mr-2 h-4 w-4" />
                                    {t('view_details')}
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => { setActionUser(user); setShowResetPasswordDialog(true); }}>
                                    <Key className="mr-2 h-4 w-4" />
                                    {t('reset_password')}
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => { setActionUser(user); setShowToggleAdminDialog(true); }}>
                                    {user.is_admin ? (
                                      <>
                                        <ShieldOff className="mr-2 h-4 w-4" />
                                        {t('remove_admin')}
                                      </>
                                    ) : (
                                      <>
                                        <Shield className="mr-2 h-4 w-4" />
                                        {t('make_admin')}
                                      </>
                                    )}
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => { setActionUser(user); setShowToggleStatusDialog(true); }}>
                                    {user.is_active ? (
                                      <>
                                        <UserX className="mr-2 h-4 w-4" />
                                        {t('deactivate')}
                                      </>
                                    ) : (
                                      <>
                                        <UserCheck className="mr-2 h-4 w-4" />
                                        {t('activate')}
                                      </>
                                    )}
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}

              {/* Pagination Controls */}
              {pagesCnt > 1 && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <p className="text-sm font-medium">{t('rows_per_page')}</p>
                    <select
                      value={params.page_size}
                      onChange={(e) => setParams(prev => ({ ...prev, page_size: Number(e.target.value), page: 0 }))}
                      className="h-8 w-[70px] rounded border border-slate-300 px-2 text-sm"
                    >
                      {[5, 10, 20, 30, 40, 50].map((pageSize) => (
                        <option key={pageSize} value={pageSize}>
                          {pageSize}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex w-[100px] items-center justify-center text-sm font-medium">
                    {t('pagination', {
                      current_page: Math.floor(params.page / params.page_size) + 1,
                      total_pages: pagesCnt
                    })}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="outline"
                      className="h-8 w-8 p-0"
                      onClick={() => setParams(prev => ({ ...prev, page: 0 }))}
                      disabled={params.page <= 0}
                    >
                      <span className="sr-only">{t('go_first_page')}</span>
                      ««
                    </Button>
                    <Button
                      variant="outline"
                      className="h-8 w-8 p-0"
                      onClick={() => setParams(prev => ({ ...prev, page: Math.max(0, prev.page - prev.page_size) }))}
                      disabled={params.page <= 0}
                    >
                      <span className="sr-only">{t('go_prev_page')}</span>
                      ‹
                    </Button>
                    <Button
                      variant="outline"
                      className="h-8 w-8 p-0"
                      onClick={() => setParams(prev => ({ ...prev, page: Math.min((pagesCnt - 1) * prev.page_size, prev.page + prev.page_size) }))}
                      disabled={params.page >= (pagesCnt - 1) * params.page_size}
                    >
                      <span className="sr-only">{t('go_next_page')}</span>
                      ›
                    </Button>
                    <Button
                      variant="outline"
                      className="h-8 w-8 p-0"
                      onClick={() => setParams(prev => ({ ...prev, page: (pagesCnt - 1) * prev.page_size }))}
                      disabled={params.page >= (pagesCnt - 1) * params.page_size}
                    >
                      <span className="sr-only">{t('go_last_page')}</span>
                      »»
                    </Button>
                  </div>
                </div>
              )}
            </div>


          </div>

          {/* User Details Dialog */}
          <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-slate-900">
                  <User className="h-5 w-5 text-slate-600" />
                  {t('user_details')}
                </DialogTitle>
              </DialogHeader>
              {selectedUser && (
                <div className="space-y-6">
                  {/* User Avatar and Basic Info */}
                  <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="h-16 w-16 rounded-full bg-slate-200 flex items-center justify-center">
                      <User className="h-8 w-8 text-slate-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-semibold text-slate-900">{selectedUser.name}</h3>
                      <p className="text-slate-600">@{selectedUser.username || 'no-username'}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <Mail className="h-4 w-4 text-slate-500" />
                        <span className="text-sm text-slate-600">{selectedUser.email || t('no_email')}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge
                        variant={selectedUser.is_active ? 'default' : 'secondary'}
                        className={selectedUser.is_active ? 'bg-slate-900 hover:bg-slate-800' : 'bg-slate-300 text-slate-700'}
                      >
                        {selectedUser.is_active ? t('active') : t('inactive')}
                      </Badge>
                    </div>
                  </div>

                  {/* Permissions */}
                  <div className="space-y-3">
                    <h4 className="text-sm font-medium text-slate-900">{t('permissions')}</h4>
                    <div className="flex gap-2">
                      {selectedUser.is_super_admin && (
                        <Badge className="bg-slate-800 hover:bg-slate-700 text-white">
                          <Crown className="h-3 w-3 mr-1" />
                          {t('super_admin')}
                        </Badge>
                      )}
                      {selectedUser.is_admin && !selectedUser.is_super_admin && (
                        <Badge variant="outline" className="border-slate-300 text-slate-700">
                          <Shield className="h-3 w-3 mr-1" />
                          {t('admin')}
                        </Badge>
                      )}
                      {!selectedUser.is_admin && !selectedUser.is_super_admin && (
                        <Badge variant="secondary" className="bg-slate-100 text-slate-600">
                          <User className="h-3 w-3 mr-1" />
                          {t('user')}
                        </Badge>
                      )}
                    </div>
                  </div>

                  {/* Details Grid */}
                  <div className="grid grid-cols-2 gap-6">
                    <div className="space-y-1">
                      <Label className="text-sm font-medium text-slate-600">{t('cabinet')}</Label>
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-slate-500" />
                        <span className="text-sm text-slate-900">{selectedUser.cabinet_name || t('no_cabinet')}</span>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-sm font-medium text-slate-600">{t('role')}</Label>
                      <span className="text-sm text-slate-900">{selectedUser.role_name || t('no_role')}</span>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-sm font-medium text-slate-600">{t('created_at')}</Label>
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-slate-500" />
                        <span className="text-sm text-slate-900">{selectedUser.created_at ? timestamp_to_date(Number(selectedUser.created_at)).toLocaleDateString() : t('unknown')}</span>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-sm font-medium text-slate-600">{t('last_login')}</Label>
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-slate-500" />
                        <span className="text-sm text-slate-900">{selectedUser.last_login_display || t('never')}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowDetailsDialog(false)} className="border-slate-300 text-slate-700 hover:bg-slate-50">
                  {t('close')}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Reset Password Dialog */}
          <Dialog open={showResetPasswordDialog} onOpenChange={setShowResetPasswordDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-slate-900">
                  <Key className="h-5 w-5 text-slate-600" />
                  {t('reset_password')}
                </DialogTitle>
                <DialogDescription className="text-slate-600">
                  {t('reset_password_for')} <strong className="text-slate-900">{actionUser?.name}</strong>
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="new_password" className="text-sm font-medium text-slate-700">
                    {t('new_password')}
                  </Label>
                  <Input
                    id="new_password"
                    type="password"
                    value={resetPasswordData.new_password}
                    onChange={(e) => setResetPasswordData({
                      ...resetPasswordData,
                      new_password: e.target.value
                    })}
                    className="border-slate-300 focus:border-slate-500 focus:ring-slate-500"
                    placeholder={t('enter_new_password')}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm_password" className="text-sm font-medium text-slate-700">
                    {t('confirm_password')}
                  </Label>
                  <Input
                    id="confirm_password"
                    type="password"
                    value={resetPasswordData.confirm_password}
                    onChange={(e) => setResetPasswordData({
                      ...resetPasswordData,
                      confirm_password: e.target.value
                    })}
                    className="border-slate-300 focus:border-slate-500 focus:ring-slate-500"
                    placeholder={t('confirm_new_password')}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setShowResetPasswordDialog(false)}
                  className="border-slate-300 text-slate-700 hover:bg-slate-50"
                >
                  {t('cancel')}
                </Button>
                <Button
                  onClick={handleResetPassword}
                  disabled={!resetPasswordData.new_password || !resetPasswordData.confirm_password}
                  className="bg-slate-900 hover:bg-slate-800"
                >
                  {t('reset_password')}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Toggle Admin Dialog */}
          <Dialog open={showToggleAdminDialog} onOpenChange={setShowToggleAdminDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-slate-900">
                  {actionUser?.is_admin ? (
                    <ShieldOff className="h-5 w-5 text-slate-600" />
                  ) : (
                    <Shield className="h-5 w-5 text-slate-600" />
                  )}
                  {actionUser?.is_admin ? t('remove_admin_privileges') : t('grant_admin_privileges')}
                </DialogTitle>
                <DialogDescription className="text-slate-600">
                  {actionUser?.is_admin
                    ? t('remove_admin_privileges_description')
                    : t('grant_admin_privileges_description')
                  } <strong className="text-slate-900">{actionUser?.name}</strong>
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setShowToggleAdminDialog(false)}
                  className="border-slate-300 text-slate-700 hover:bg-slate-50"
                >
                  {t('cancel')}
                </Button>
                <Button
                  onClick={handleToggleAdmin}
                  className={actionUser?.is_admin
                    ? "bg-slate-600 hover:bg-slate-700"
                    : "bg-slate-900 hover:bg-slate-800"
                  }
                >
                  {actionUser?.is_admin ? t('remove_admin') : t('make_admin')}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Toggle Status Dialog */}
          {/* Create User Dialog */}
          <Dialog open={showCreateUserDialog} onOpenChange={setShowCreateUserDialog}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-slate-900">
                  <Plus className="h-5 w-5 text-slate-600" />
                  {t('create_user')}
                </DialogTitle>
                <DialogDescription className="text-slate-600">
                  {t('create_new_user_description')}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">{t('name')} *</Label>
                  <Input
                    id="name"
                    value={createUserData.name}
                    onChange={(e) => setCreateUserData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder={t('enter_name')}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="username">{t('username')}</Label>
                  <Input
                    id="username"
                    value={createUserData.username}
                    onChange={(e) => setCreateUserData(prev => ({ ...prev, username: e.target.value }))}
                    placeholder={t('enter_username')}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">{t('email')}</Label>
                  <Input
                    id="email"
                    type="email"
                    value={createUserData.email}
                    onChange={(e) => setCreateUserData(prev => ({ ...prev, email: e.target.value }))}
                    placeholder={t('enter_email')}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cabinet">{t('cabinet')} *</Label>
                  <Select
                    value={createUserData.cabinet}
                    onValueChange={(value) => setCreateUserData(prev => ({ ...prev, cabinet: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('select_cabinet')} />
                    </SelectTrigger>
                    <SelectContent>
                      {cabinets.map((cabinet) => (
                        <SelectItem key={cabinet.id} value={cabinet.id}>
                          {cabinet.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="role">{t('role')} *</Label>
                  <Select
                    value={createUserData.role?.toString()}
                    onValueChange={(value) => setCreateUserData(prev => ({ ...prev, role: parseInt(value) }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('select_role')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">{t('admin')}</SelectItem>
                      <SelectItem value="2">{t('user')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">{t('password')} *</Label>
                  <Input
                    id="password"
                    type="password"
                    value={createUserData.password}
                    onChange={(e) => setCreateUserData(prev => ({ ...prev, password: e.target.value }))}
                    placeholder={t('enter_password')}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm_password">{t('confirm_password')} *</Label>
                  <Input
                    id="confirm_password"
                    type="password"
                    value={createUserData.confirm_password}
                    onChange={(e) => setCreateUserData(prev => ({ ...prev, confirm_password: e.target.value }))}
                    placeholder={t('confirm_password')}
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="is_active"
                    checked={createUserData.is_active}
                    onCheckedChange={(checked) => setCreateUserData(prev => ({ ...prev, is_active: !!checked }))}
                  />
                  <Label htmlFor="is_active">{t('active_user')}</Label>
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setShowCreateUserDialog(false)}
                  className="border-slate-300 text-slate-700 hover:bg-slate-50"
                >
                  {t('cancel')}
                </Button>
                <Button
                  onClick={handleCreateUser}
                  className="bg-slate-900 hover:bg-slate-800"
                >
                  {t('create_user')}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={showToggleStatusDialog} onOpenChange={setShowToggleStatusDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-slate-900">
                  {actionUser?.is_active ? (
                    <UserX className="h-5 w-5 text-slate-600" />
                  ) : (
                    <UserCheck className="h-5 w-5 text-slate-600" />
                  )}
                  {actionUser?.is_active ? t('deactivate_user') : t('activate_user')}
                </DialogTitle>
                <DialogDescription className="text-slate-600">
                  {actionUser?.is_active
                    ? t('deactivate_user_description')
                    : t('activate_user_description')
                  } <strong className="text-slate-900">{actionUser?.name}</strong>
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setShowToggleStatusDialog(false)}
                  className="border-slate-300 text-slate-700 hover:bg-slate-50"
                >
                  {t('cancel')}
                </Button>
                <Button
                  onClick={handleToggleStatus}
                  className={actionUser?.is_active
                    ? "bg-red-600 hover:bg-red-700 text-white"
                    : "bg-slate-900 hover:bg-slate-800"
                  }
                >
                  {actionUser?.is_active ? t('deactivate') : t('activate')}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </Main>
      </div>
    </div>
  );
}